/**
 * Target list builders for the workspace rail.
 *
 * Separate from the component so the rail file exports only a component: Fast
 * Refresh cannot preserve state in a module that also exports helpers.
 */
import type { PnidEquipment, PnidInstrument, PnidNode } from '../types';

/** Three states, as the context bar's badge had them: an SME needs to tell a
 *  line that is signed off from one that has merely been touched. */
export type TargetStatus = 'reviewed' | 'marked' | 'unmarked';

export type TargetRailItem = {
  id: string;
  label: string;
  sub?: string;
  status: TargetStatus;
};

/** Which end of the list the work sits at. Marking runs down the unmarked ones,
 *  so that is the default; reviewing runs down what is already marked. */
export type TargetSortOrder = 'unmarked' | 'marked';

const MARKED_STATUSES = ['in_progress', 'final', 'final_and_reviewed'];

function targetStatus(status: string): TargetStatus {
  if (status === 'final_and_reviewed') return 'reviewed';
  return MARKED_STATUSES.includes(status) ? 'marked' : 'unmarked';
}

/** Equipment: AI-identified first (the queue of work), then by tag. */
export function equipmentTargets(equipments: PnidEquipment[]): TargetRailItem[] {
  return [...equipments]
    .sort((a, b) => {
      if (a.status === 'ai_identified' && b.status !== 'ai_identified') return -1;
      if (a.status !== 'ai_identified' && b.status === 'ai_identified') return 1;
      return (a.equipment_no || '').localeCompare(b.equipment_no || '');
    })
    .map((eq) => ({
      id: eq.id,
      label: eq.equipment_no || 'Untitled',
      sub: eq.equipment_type || undefined,
      status: targetStatus(eq.status),
    }));
}

/** Lines, in the order the caller is working: unmarked first while marking,
 *  marked first while reviewing. Ties break on line number. */
export function nodeTargets(
  nodes: PnidNode[],
  order: TargetSortOrder = 'unmarked',
): TargetRailItem[] {
  return [...nodes]
    .sort((a, b) => {
      const aMarked = a.status !== 'ai_identified';
      const bMarked = b.status !== 'ai_identified';
      if (aMarked !== bMarked) {
        if (order === 'unmarked') return aMarked ? 1 : -1;
        return aMarked ? -1 : 1;
      }
      return a.node_no - b.node_no;
    })
    .map((node) => ({
      id: node.id,
      label: `#${node.node_no}${node.line_no ? ` ${node.line_no}` : ''}`,
      sub:
        node.source_ref && node.dest_ref
          ? `${node.source_ref} → ${node.dest_ref}`
          : undefined,
      status: targetStatus(node.status),
    }));
}

export type TargetRailGroup = {
  id: string;
  label: string;
  items: TargetRailItem[];
};

/** The API owns eligibility and type order; the rail preserves each annotation. */
export function instrumentTargetGroups(instruments: PnidInstrument[]): TargetRailGroup[] {
  const groups = new Map<string, TargetRailGroup>();
  for (const instrument of instruments) {
    const mirroredType = instrument.core_fields?.instrument_type;
    const mirroredText = instrument.core_fields?.instrument_text;
    const type = instrument.instrument_type?.trim()
      || (typeof mirroredType === 'string' ? mirroredType.trim() : '');
    const label = instrument.instrument_text?.trim()
      || (typeof mirroredText === 'string' ? mirroredText.trim() : '')
      || instrument.generated_id || type;
    if (!groups.has(type)) groups.set(type, { id: type, label: type, items: [] });
    groups.get(type)!.items.push({
      id: instrument.id, label, sub: type, status: targetStatus(instrument.status),
    });
  }
  return [...groups.values()].map((group) => ({
    ...group,
    items: group.items.sort((left, right) =>
      left.label.localeCompare(right.label) || left.id.localeCompare(right.id)),
  }));
}
